import type { ListBuilderConfiguration } from '../types';

/**
 * Price Contract Client list configuration
 * Shows clients associated with a price contract.
 * Data is served by ClientsService.getAll() with a priceContractId filter,
 * so field references use the client objectTable with flat field names.
 */
export const PRICE_CONTRACT_CLIENT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-price-contract-clients-all',
			cacheTTL: 600000,
			name: 'All Price Contract Clients',
			type: 'default',
			active: true,
			bulkActionTemplate: 'priceContractClients',
			// Users without clients:detail:any only see their own book of clients.
			// Lock the salespeople filter to "Me" so the server-enforced filter
			// can match what the UI shows.
			filterRules: {
				salespeople: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'client-address',
					fields: [
						{
							id: 'client-name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'client',
								idField: 'id',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'address1',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h1-1', text: 'Client' },
						{ id: 'h1-2', text: 'Address' },
					],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'client',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'external-id',
					fields: [
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'externalId',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'ERP #' }],
					minWidth: 120,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'type',
					fields: [
						{
							id: 'lead-customer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'leadOrCustomer',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'business-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'businessType',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h3-1', text: 'Lead/Cust' },
						{ id: 'h3-2', text: 'Business Type' },
					],
					minWidth: 150,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'sales-rep',
					fields: [
						{
							id: 'sales-rep-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'salespersonName',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'alt-sales-rep-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'alternateSalespersonName',
									objectTable: 'client',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h4-1', text: 'Sales Rep' },
						{ id: 'h4-2', text: 'Alternate' },
					],
					minWidth: 150,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 60,
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					widthConfig: {
						mode: 'fixed',
					},
				},
			],
			defaultSortId: 'column-client-address-asc',
			// No authored requiredFields: the server derives the field set from the
			// resolved list's columns, so columns added via Configure List get
			// their data selected without a code change (PD-15409).
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'clientSearch',
			'leadOrCustomer',
			'businessTypes',
			'groups',
			'geography',
			'salesStatus',
			'salespeople',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			// The tab returns plain client rows straight from ClientsService, so
			// `id` is the client's own id. Declaring it opens the field picker to
			// the whole client table instead of only the curated list below
			// (PD-14150).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'externalId', fieldLabel: 'ERP #' },
				{ fieldName: 'address1', fieldLabel: 'Address' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead/Cust' },
				{ fieldName: 'businessType', fieldLabel: 'Business Type' },
			],
		},
	],
};
